/**
 * /account/delete — public landing page.
 *
 * Required by Google Play's Data deletion review per their spec:
 *   1. References the app name shown on the store listing ("WYDM")
 *   2. Prominently features the deletion steps
 *   3. Specifies what's deleted, what's kept, and any retention period
 *
 * Apple App Store Connect doesn't require a URL — it just asks whether
 * deletion is available in-app. But this page covers both: any reviewer
 * who lands here gets the full picture.
 *
 * Public — no sign-in gate. Users who can't sign in have the
 * support@wydm.app fallback.
 */
import { Link } from 'react-router-dom';

import { sessionStore } from '@/lib/api';

export function AccountDeletePage() {
  const signedIn = !!sessionStore.getAccess();

  return (
    <main style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px' }}>
      {/* Wordmark */}
      <Link to="/" style={wordmarkLink}>
        <span className="serif" style={wordmarkText}>wydm</span>
        <span style={wordmarkDot} />
      </Link>

      <h1 className="serif" style={pageTitle}>
        <span style={{ fontStyle: 'italic' }}>Delete</span> your WYDM account.
      </h1>
      <p style={lede}>
        You can permanently delete your WYDM account and the data we hold
        about you. Below: how to do it, what we delete, and what we have
        to keep.
      </p>

      {/* ── How to delete ─────────────────────────────────────────── */}
      <h2 className="serif" style={sectionH2}>How to delete</h2>

      <div style={card}>
        <div className="mono" style={eyebrowText}>In the mobile app (iOS / Android)</div>
        <ol style={ol}>
          <li>Open WYDM and sign in.</li>
          <li>Tap <strong>Settings</strong> (bottom tab bar or profile menu).</li>
          <li>Scroll to the <strong>Danger zone</strong> section.</li>
          <li>Tap <strong>Delete account…</strong></li>
          <li>(Optional) Pick a reason from the list — it's anonymous; helps us improve.</li>
          <li>Type <code style={inlineCode}>delete my account</code> in the confirmation field.</li>
          <li>Tap <strong>Yes, delete my account</strong>.</li>
        </ol>
      </div>

      <div style={card}>
        <div className="mono" style={eyebrowText}>On the web (wydm.app)</div>
        <ol style={ol}>
          <li>{signedIn ? (
            <>Go to <Link to="/settings" style={inlineLink}>Settings</Link>.</>
          ) : (
            <>Sign in at <Link to="/auth/signin" style={inlineLink}>wydm.app/auth/signin</Link>, then go to <Link to="/settings" style={inlineLink}>Settings</Link>.</>
          )}</li>
          <li>Scroll to the <strong>Danger zone</strong> section.</li>
          <li>Click <strong>Delete account…</strong></li>
          <li>(Optional) Pick a reason from the dropdown.</li>
          <li>Type <code style={inlineCode}>delete my account</code> in the confirmation field.</li>
          <li>Click <strong>Yes, delete my account</strong>.</li>
        </ol>
      </div>

      <div style={fallbackBox}>
        <strong>Can't sign in?</strong>
        <div style={{ marginTop: 6 }}>
          Try the <Link to="/auth/forgot" style={inlineLink}>password reset</Link> first.
          If you're still locked out, email{' '}
          <a href="mailto:support@wydm.app?subject=Account%20deletion%20request"
             style={inlineLink}>support@wydm.app</a> from the address on your
          account and we'll process the deletion for you.
        </div>
      </div>

      {/* ── What we delete ───────────────────────────────────────── */}
      <h2 className="serif" style={sectionH2}>What we delete</h2>
      <p style={para}>
        Your profile becomes invisible to everyone <strong>immediately</strong>.
        The hard delete runs after a <strong>30-day grace period</strong> — if
        you change your mind in that window, email us and we'll restore
        the account. After 30 days the following are removed permanently
        from our systems and our backups within the next 90 days:
      </p>
      <ul style={ul}>
        <li>Your account record — email, sign-in credentials, OAuth provider links, display name, date of birth.</li>
        <li>Your profile — bio, gender, looking-for choices, public slug.</li>
        <li>All photos you uploaded.</li>
        <li>Your Spotify connection and any cached top tracks/artists.</li>
        <li>All direct-message threads and messages you sent or received.</li>
        <li>Your notification inbox and registered devices for push notifications.</li>
        <li>All active sign-in sessions and refresh tokens.</li>
        <li>Your premium entitlement and tier records.</li>
        <li>The personal identifier on any ratings you gave others. (The ratings themselves remain on the profiles you rated, but become fully anonymous — even we can no longer link them to you.)</li>
      </ul>

      {/* ── What we keep ─────────────────────────────────────────── */}
      <h2 className="serif" style={sectionH2}>What we keep, and why</h2>
      <p style={para}>
        A small amount of data survives the hard delete. We're legally
        required to retain these:
      </p>
      <ul style={ul}>
        <li>
          <strong>Payment records</strong> (the dates, amounts, vendor IDs,
          and outcomes of any subscriptions you bought). Tax authorities
          and chargeback processes require us to keep these.
          <br />
          <span style={meta}>
            Retention: 7 years (US tax) or 10 years (EU VAT records), whichever
            applies to your jurisdiction.
          </span>
        </li>
        <li>
          <strong>Webhook delivery log</strong> (payment provider events that
          touched your account). Required for fraud investigations and
          financial reconciliation.
          <br />
          <span style={meta}>
            Retention: 7 years. The personal link to you (user_id) is
            unset; only vendor identifiers remain.
          </span>
        </li>
        <li>
          <strong>Security audit log</strong> (sign-in attempts, password
          resets, admin actions touching your account). Required for
          security investigations.
          <br />
          <span style={meta}>
            Retention: 2 years from the date of the event.
          </span>
        </li>
      </ul>

      {/* ── Subscriptions ────────────────────────────────────────── */}
      <h2 className="serif" style={sectionH2}>If you have an active subscription</h2>
      <p style={para}>
        On account deletion:
      </p>
      <ul style={ul}>
        <li>
          <strong>Web subscriptions (via Stripe)</strong> are scheduled to
          cancel at the end of your current paid period. No further
          charges. You keep Premium until that date.
        </li>
        <li>
          <strong>App Store subscriptions (iOS)</strong> can only be
          cancelled by you, from <em>Settings → [your Apple ID] →
          Subscriptions → WYDM Premium → Cancel</em>. We can't cancel
          App Store subscriptions on your behalf.
        </li>
        <li>
          <strong>Google Play subscriptions (Android)</strong> can only be
          cancelled by you, from <em>Play Store → Profile → Payments &amp;
          subscriptions → Subscriptions → WYDM Premium → Cancel</em>. We
          can't cancel Play Store subscriptions on your behalf.
        </li>
      </ul>

      <div style={footer}>
        Questions? Email{' '}
        <a href="mailto:support@wydm.app" style={inlineLink}>support@wydm.app</a>.
        See also our{' '}
        <a href="/privacy" style={inlineLink}>Privacy Policy</a> and{' '}
        <a href="/terms" style={inlineLink}>Terms</a>.
      </div>
    </main>
  );
}

// ── styles ────────────────────────────────────────────────────────────

const wordmarkLink: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'baseline', gap: 4,
  textDecoration: 'none', color: 'var(--ink)',
};
const wordmarkText: React.CSSProperties = {
  fontSize: 28, fontWeight: 500, fontStyle: 'italic', letterSpacing: -0.5,
};
const wordmarkDot: React.CSSProperties = {
  width: 8, height: 8, borderRadius: 8, background: 'var(--coral)',
  display: 'inline-block',
};

const pageTitle: React.CSSProperties = {
  fontSize: 44, fontWeight: 500, lineHeight: 1.1, marginTop: 32, marginBottom: 12,
  letterSpacing: -0.5,
};
const lede: React.CSSProperties = {
  fontSize: 16, color: 'var(--ink-2)', lineHeight: 1.55, marginBottom: 36,
};

const sectionH2: React.CSSProperties = {
  fontSize: 24, fontWeight: 500, fontStyle: 'italic',
  marginTop: 40, marginBottom: 14,
};

const card: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: 18,
  padding: '22px 24px',
  marginBottom: 14,
  boxShadow: 'var(--shadow-card)',
};

const eyebrowText: React.CSSProperties = {
  fontSize: 11, color: 'var(--coral)', letterSpacing: 2,
  textTransform: 'uppercase', marginBottom: 14,
};

const ol: React.CSSProperties = {
  paddingLeft: 22, margin: 0,
  fontSize: 15, lineHeight: 1.7, color: 'var(--ink)',
};
const ul: React.CSSProperties = {
  paddingLeft: 22, margin: 0,
  fontSize: 15, lineHeight: 1.7, color: 'var(--ink)',
};

const para: React.CSSProperties = {
  fontSize: 15, color: 'var(--ink-2)', lineHeight: 1.6, marginBottom: 14,
};

const meta: React.CSSProperties = {
  fontSize: 12.5, color: 'var(--ink-3)', display: 'inline-block', marginTop: 4,
};

const inlineCode: React.CSSProperties = {
  background: 'rgba(208,64,48,0.08)',
  padding: '1px 6px', borderRadius: 4,
  fontFamily: 'ui-monospace, monospace', fontSize: 13,
};

const inlineLink: React.CSSProperties = {
  color: 'var(--coral)', textDecoration: 'underline', fontWeight: 600,
};

const fallbackBox: React.CSSProperties = {
  background: 'rgba(232, 106, 76, 0.06)',
  border: '1px solid rgba(232, 106, 76, 0.18)',
  borderRadius: 12,
  padding: '16px 18px',
  fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.55,
  marginBottom: 16, marginTop: 8,
};

const footer: React.CSSProperties = {
  marginTop: 48, paddingTop: 24,
  borderTop: '1px solid var(--line)',
  fontSize: 13, color: 'var(--ink-3)', lineHeight: 1.6,
};
